import { buildApiUrl } from "../../../config.js";
import { useEffect, useState } from "react";
import { MdOutlinePieChartOutline, MdSpaceDashboard } from "react-icons/md";
import { FaArrowTrendUp, FaCartShopping, FaRankingStar } from "react-icons/fa6";
import { BiPurchaseTagAlt } from "react-icons/bi";
import { TbActivity } from "react-icons/tb";
import { SiVictoriametrics } from "react-icons/si";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  UserPlus,
  Package,
  DollarSign,
  ShoppingCart,
  MapPin,
  Clock,
  CreditCard,
  AlertTriangle,
  Truck,
  IndianRupee,
} from "lucide-react";
import axios from "axios";
import { PuffLoader, BarLoader, CircleLoader } from "react-spinners";
import { ColorRing } from "react-loader-spinner";
import convertToCr from "./../../ui/convertToCr";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalItems: 0,
    totalAmountOfDispatchedOrders: 0,
    totalOrders: 0,
    monthlyPayments: [],
    topItems: [],
    orderStatusData: [],
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [orderMetrics, setOrderMetrics] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoadingDashboard(true);
        const response = await axios.get(
          buildApiUrl("/api/report/dashboard"),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setDashboardData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoadingDashboard(false);
      }
    };

    const fetchOrderMetricsData = async () => {
      try {
        setIsLoadingMetrics(true);
        const response = await axios.get(
          buildApiUrl("/api/report/order-metric/recentOrder"),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const { recentOrders, recentActivities, orderMetrics } = response.data;
        setRecentOrders(recentOrders);
        setRecentActivities(recentActivities);
        setOrderMetrics(orderMetrics);
      } catch (error) {
        console.error("Error fetching order metrics data:", error);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    fetchDashboardData();
    fetchOrderMetricsData();
  }, [token]);

  // Data for charts
  const revenueData = dashboardData.monthlyPayments.map((payment) => ({
    name: payment.month,
    revenue: payment.totalPaid,
  }));

  const topItemsData = dashboardData.topItems.map((item) => ({
    name: item._id,
    quantity: item.totalQty,
  }));

  const orderStatusData = dashboardData.orderStatusData.map((status) => ({
    name: status.name,
    value: parseFloat(status.value),
  }));

  // Loader Component
  const ChartLoader = ({ type }) => {
    switch (type) {
      case "line":
        return (
          <div className="flex justify-center items-center h-[300px]">
            <PuffLoader color="#8884d8" size={60} />
          </div>
        );
      case "bar":
        return (
          <div className="flex justify-center items-center h-[300px]">
            <BarLoader color="#8884d8" width={150} />
          </div>
        );
      case "pie":
        return (
          <div className="flex justify-center items-center h-[250px]">
            <CircleLoader color="#8884d8" size={60} />
          </div>
        );
      default:
        return null;
    }
  };

  // if (isLoadingDashboard) {
  //   return (
  //     <div className="flex justify-center items-center min-h-screen">
  //       <ColorRing
  //         visible={true}
  //         height="150"
  //         width="150"
  //         ariaLabel="color-ring-loading"
  //         wrapperStyle={{}}
  //         wrapperClass="color-ring-wrapper"
  //         colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
  //       />
  //     </div>
  //   );
  // }

  return (
    <div className="">
      <header className="mb-2">
        <h1 className="text-lg md:text-xl lg:text-2xl flex gap-2 items-center font-semibold text-red-600">
          <MdSpaceDashboard />
          Admin Dashboard
        </h1>
      </header>

      {/* Stats Section */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
        {[
          {
            title: "Total Users",
            icon: UserPlus,
            value: dashboardData.totalUsers,
            color: "text-blue-600",
          },
          {
            title: "Products",
            icon: Package,
            value: dashboardData.totalItems,
            color: "text-green-600",
          },
          {
            title: "Revenue",
            icon: IndianRupee,
            value: `₹${convertToCr(
              dashboardData.totalAmountOfDispatchedOrders
            )}`,
            color: "text-yellow-600",
          },
          {
            title: "Orders",
            icon: ShoppingCart,
            value: dashboardData.totalOrders,
            color: "text-red-600",
          },
        ].map((item) => (
          <div
            key={item.title}
            className="p-2 md:p-4 rounded-xl shadow-md bg-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {item.title}
                </p>
                <p
                  className={`text-lg md:text-xl lg:text-2xl font-bold ${item.color}`}
                >
                  {isLoadingDashboard ? (
                    <PuffLoader
                      color={item.color.replace("text-", "#")}
                      size={24}
                    />
                  ) : (
                    item.value
                  )}
                </p>
              </div>
              <item.icon className="text-sm lg:text-lg text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
        <div className="p-4 rounded-xl shadow-lg bg-white">
          <h2 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
            <FaArrowTrendUp />
            Revenue Trend
          </h2>
          {isLoadingDashboard ? (
            <ChartLoader type="line" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-4 rounded-xl shadow-lg bg-white">
          <h2 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
            <FaRankingStar />
            Top 7 Items Bought
          </h2>
          {isLoadingDashboard ? (
            <ChartLoader type="bar" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topItemsData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantity" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Orders and Activities Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
        <div className="p-4 rounded-xl shadow-lg bg-white">
          <h2 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
            <BiPurchaseTagAlt />
            Recent Orders
          </h2>
          {isLoadingMetrics ? (
            <div className="flex justify-center items-center h-[200px]">
              <PuffLoader color="#8884d8" size={60} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b text-sm md:text-md ">
                    <th className="text-left py-1">#</th>
                    <th className="text-left py-2">Order ID</th>
                    <th className="text-left py-2">Client</th>
                    <th className="text-left py-2">Amount</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order, index) => (
                    <tr key={order.id} className="border-b text-sm md:text-md">
                      <td className="py-1">{index + 1}</td>
                      <td className="py-1 font-semibold text-red-500 uppercase">
                        {order.id.slice(-5)}
                      </td>
                      <td className="py-1">{order.client}</td>
                      <td className="py-1">
                        ₹{convertToCr(order.amount.toLocaleString())}
                      </td>
                      <td className="py-0.5">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] ${
                            order.status === "Accept"
                              ? "bg-green-200 text-green-800"
                              : "bg-red-200 text-red-800"
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 rounded-xl shadow-lg bg-white">
          <h2 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
            <TbActivity />
            Recent Activities
          </h2>
          {isLoadingMetrics ? (
            <div className="flex justify-center items-center h-[200px]">
              <PuffLoader color="#8884d8" size={60} />
            </div>
          ) : (
            <ul className="space-y-4">
              {recentActivities.map((activity, index) => (
                <li key={index} className="flex items-center space-x-3">
                  <div className="bg-purple-100 p-2 rounded-full">
                    <activity.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {activity.action}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.timestamp}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Order Management Section */}
      <div className="p-6 rounded-xl shadow-lg bg-white mb-2">
        <h2 className="text-lg md:text-xl lg:text-2xl flex gap-2 mb-4 items-center font-semibold text-gray-600">
          <FaCartShopping />
          Order Management
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Order Status Distribution Section */}
          <div>
            <h3 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
              <MdOutlinePieChartOutline />
              Order Status Distribution
            </h3>
            {isLoadingDashboard ? (
              <ChartLoader type="pie" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Order Metrics Section */}
          <div>
            <h3 className="text-sm md:text-lg lg:text-xl flex gap-2 mb-4 items-center font-semibold text-red-600">
              <SiVictoriametrics />
              Order Metrics
            </h3>
            {isLoadingMetrics ? (
              <div className="flex justify-center items-center h-[250px]">
                <PuffLoader color="#8884d8" size={60} />
              </div>
            ) : (
              <ul className="space-y-4">
                {orderMetrics.map((metric, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="bg-indigo-100 p-2 rounded-full">
                      <metric.icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        {metric.label}
                      </p>
                      <p className="text-lg font-semibold text-gray-800">
                        {metric.value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
